import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMutationObserver } from '@vueuse/core'

import type { Ref } from 'vue'

import { useFeatureTourStore } from './store'

interface FeatureTourSpotPayload {
  id: Ref<string>
  targetSelector?: Ref<string>
}

export const useFeatureTourSpot = (payload: FeatureTourSpotPayload) => {
  const { id, targetSelector } = payload
  const store = useFeatureTourStore()

  const root = ref<HTMLElement | null>(null)
  const registeredElement = ref<HTMLElement | null>(null)

  const isActiveSpot = computed(() => {
    if (!store.isActive || !store.isVisible) {
      return false
    }

    return store.getCurrentStep?.spotId === id.value
  })

  const unregisterRegisteredElement = (): void => {
    if (registeredElement.value && store.spotRegistry.get(id.value) === registeredElement.value) {
      store.unregisterSpot(id.value)
    }

    registeredElement.value = null
  }

  const getSpotElement = (): HTMLElement | null => {
    const rootElement = root.value
    if (!rootElement) {
      return null
    }

    if (targetSelector?.value) {
      const selectedElement = rootElement.querySelector(targetSelector.value)
      return selectedElement instanceof HTMLElement ? selectedElement : null
    }

    const firstChild = rootElement.firstElementChild
    return firstChild instanceof HTMLElement ? firstChild : null
  }

  const syncSpot = (): void => {
    const spotElement = getSpotElement()

    if (!spotElement) {
      unregisterRegisteredElement()
      return
    }

    if (registeredElement.value === spotElement) {
      return
    }

    unregisterRegisteredElement()
    store.registerSpot(id.value, spotElement)
    registeredElement.value = spotElement
  }

  useMutationObserver(root, () => {
    syncSpot()
  }, {
    childList: true,
    subtree: true,
  })

  watch(id, (newId, prevId) => {
    if (registeredElement.value && store.spotRegistry.get(prevId) === registeredElement.value) {
      store.unregisterSpot(prevId)
      registeredElement.value = null
    }
    syncSpot()
  })

  watch(() => targetSelector?.value, () => {
    syncSpot()
  })

  onMounted(async () => {
    await nextTick()
    syncSpot()
  })

  onBeforeUnmount(() => {
    unregisterRegisteredElement()
  })

  return {
    root,
    isActiveSpot,
  }
}
